import { useMemo } from 'react'
import { TruncatedText } from '@/components/commons/truncated-text'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { InfoCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip } from '@/components/ui/tooltip'
import { AttendeeNameLink } from '@/features/marquee/components/attendee-name-link'
import { TableScrollBox } from '@/features/marquee/components/table-scroll-box'
import { TableEmpty } from '@/features/marquee/components/table-states'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import {
	attendeeDetailPath,
	exportRowFix,
	exportRowSkipReasonKey,
	isExportRowMissingOwner,
	isExportRowUnresolvable,
	isExportRowUnresolved,
	isExportRowWalkIn,
	splitExportAttendee
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeCaptureMode, MarqueeExportRow } from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

/**
 * What the child-account cell reads when the row has none: the design labels
 * the gap after its lane ("pending" until resolved, "excluded" once given up
 * on) instead of a bare dash.
 */
const childAccountPlaceholderKey = (row: MarqueeExportRow): TKey | null => {
	if (isExportRowUnresolvable(row)) {
		return 'marquee:export_cell_excluded'
	}
	if (isExportRowUnresolved(row)) {
		return 'marquee:export_cell_pending'
	}
	return null
}

/** Post-download load status: what landed in the generated CSV, and what did not. */
function LoadStatusCell({ row }: { row: MarqueeExportRow }) {
	const { t } = useTranslation()
	if (isExportRowUnresolvable(row)) {
		return (
			<span className='inline-flex items-center rounded-full bg-grey-100 px-3 py-1.5 font-medium text-neutral-700 text-xs'>
				{t('marquee:export_load_status_excluded')}
			</span>
		)
	}
	return (
		<span className='inline-flex items-center rounded-full bg-success-100 px-3 py-1.5 font-medium text-success-700 text-xs'>
			{t('marquee:export_load_status_loaded')}
		</span>
	)
}

/**
 * "N. of CTAs": the row's structured CTA list (#923). The count is the cell and
 * the labels live in a bulleted tooltip (per design), so the number stays
 * verifiable without widening the column. A row with no CTA is a plain number,
 * with no hover affordance to promise a list that is not there.
 */
function CtaCountCell({ row }: { row: MarqueeExportRow }) {
	const ctas = row.ctas ?? []
	if (ctas.length === 0) {
		return <span className='text-neutral-700 text-sm tabular-nums'>0</span>
	}
	return (
		<Tooltip
			content={
				<ul className='ml-4 list-disc font-normal'>
					{ctas.map((cta) => (
						<li key={cta.code}>{cta.label}</li>
					))}
				</ul>
			}
		>
			<span className='cursor-pointer text-neutral-700 text-sm tabular-nums'>
				{ctas.length}
			</span>
		</Tooltip>
	)
}

/** The row's lane as a pill: the Status column, and the head of the mobile card. */
function StatusBadge({ row }: { row: MarqueeExportRow }) {
	const { t } = useTranslation()
	if (isExportRowUnresolvable(row)) {
		return (
			<span className='inline-flex items-center gap-1.5 rounded-full bg-grey-100 px-3 py-1.5 font-medium text-neutral-700 text-xs'>
				<span className='size-1.5 rounded-full bg-neutral-400' />
				{t('marquee:export_status_unresolvable')}
			</span>
		)
	}
	// Resolved as a host, held out by its check-in facilitator. Grey, not orange:
	// nothing on this row is wrong and nothing here fixes it, so it must not read
	// as work waiting for the operator at this desk.
	if (isExportRowMissingOwner(row)) {
		return (
			<span className='inline-flex items-center gap-1.5 rounded-full bg-grey-100 px-3 py-1.5 font-medium text-neutral-700 text-xs'>
				<span className='size-1.5 rounded-full bg-neutral-400' />
				{t('marquee:export_status_missing_mm')}
				<Tooltip
					content={
						<p>{t('marquee:export_status_missing_mm_hint')}</p>
					}
					variant='light'
				>
					<InfoCircleIcon className='fill-neutral-400' size={16} />
				</Tooltip>
			</span>
		)
	}
	// Unresolved rows (walk-in OR invitee) share the "Pending resolution" badge
	// and block the run. It carries the check-in roster's orange, the tone that
	// tab already spends on a row needing the operator, so the same fact does not
	// change color between the two surfaces.
	if (isExportRowUnresolved(row)) {
		return (
			<span className='inline-flex items-center gap-1.5 rounded-full bg-orange-600/10 px-3 py-1.5 font-medium text-orange-700 text-xs'>
				<span className='size-1.5 rounded-full bg-orange-600' />
				{t('marquee:export_status_pending')}
				{/* What is actually missing on this row: the badge is the same
				    for a walk-in with no host ID, an invitee whose account did
				    not match and a row tagged at its parent, and those need
				    different fixes. Written here from the row's status, never
				    from the backend's own `skip_reason` sentence, which is
				    English composed server-side and untranslatable. */}
				<Tooltip
					content={<p>{t(exportRowSkipReasonKey(row))}</p>}
					variant='light'
				>
					<InfoCircleIcon className='fill-orange-700' size={16} />
				</Tooltip>
			</span>
		)
	}
	if (row.disposition === 'included') {
		return (
			<span className='inline-flex items-center rounded-full bg-success-100 px-3 py-1.5 font-medium text-success-700 text-xs'>
				{t(
					// A walk-in that made it into the run carries the operator's
					// work in its label, per design.
					isExportRowWalkIn(row)
						? 'marquee:export_status_resolved_ready'
						: 'marquee:export_status_ready'
				)}
			</span>
		)
	}
	// Fallback for a row the preview left unclassified (`disposition` is optional
	// on the wire). Every classified row is caught above, so this is the shape of a
	// response we do not recognise, not a lane of its own.
	return (
		<span className='inline-flex items-center gap-1.5 rounded-full bg-grey-100 px-3 py-1.5 font-medium text-neutral-700 text-xs'>
			<span className='size-1.5 rounded-full bg-neutral-400' />
			{t('marquee:export_status_skipped')}
			<Tooltip
				content={<p>{t(exportRowSkipReasonKey(row))}</p>}
				variant='light'
			>
				<InfoCircleIcon className='fill-neutral-400' size={16} />
			</Tooltip>
		</span>
	)
}

/**
 * What the row offers the operator: the Actions column. `null` for the two
 * settled lanes (included, unclassified), which are stated by their badge and
 * need nothing done to them.
 */
function RowActions({
	row,
	onMarkUnresolvable,
	onAssignHost,
	onRestoreResolution,
	isRestoring
}: {
	row: MarqueeExportRow
	onMarkUnresolvable?: () => void
	onAssignHost?: () => void
	onRestoreResolution?: () => void
	isRestoring?: boolean
}) {
	const { t } = useTranslation()
	// Terminal, but the operator's verdict rather than a fact, so it is the one
	// lane that can be walked back. Every surface that renders a marked row now
	// offers this: here, the check-in desk and the attendees roster. The mark can
	// be made mid-event, and this tab does not open until the event has closed.
	if (isExportRowUnresolvable(row)) {
		if (!row.attendee_id) {
			return null
		}
		return (
			<Button
				className='l:w-auto w-full shrink-0'
				disabled={isRestoring}
				onClick={onRestoreResolution}
				shape='rounded'
				size='sm'
				variant='secondaryOutline'
			>
				{t('marquee:export_restore_resolution')}
				{isRestoring && <Spinner size={14} />}
			</Button>
		)
	}
	// Either population can be given up on, so the action is offered to any row
	// carrying an attendee id.
	if (isExportRowUnresolved(row)) {
		const fix = exportRowFix(row)
		// Nothing to give up on either: the host is fine and the row is held by
		// its facilitator, which is fixed by registering that MM and re-running
		// the preview. Marking it unresolvable would throw away a good row.
		if (isExportRowMissingOwner(row)) {
			return null
		}
		return (
			<div className='flex l:w-auto w-full l:flex-row flex-col l:items-center items-stretch gap-2 l:gap-3'>
				{row.attendee_id && (
					<Button
						className='l:w-auto w-full shrink-0'
						onClick={onMarkUnresolvable}
						shape='rounded'
						size='sm'
						variant='tertiary'
					>
						{t('marquee:export_mark_unresolvable')}
					</Button>
				)}
				{row.attendee_id && fix === 'assign_host' && (
					<Button
						className='l:w-auto w-full shrink-0'
						onClick={onAssignHost}
						shape='rounded'
						size='sm'
						variant='secondaryOutline'
					>
						{t('marquee:export_resolve')}
					</Button>
				)}
			</div>
		)
	}
	return null
}

interface ExportPreviewTableProps {
	data: MarqueeExportRow[]
	/** Event id — enables opening a row's attendee-detail page (rows with an id). */
	eventId?: string
	/** The event's capture mode: only a tune-up has an attendee-detail page. */
	captureMode?: MarqueeCaptureMode
	/**
	 * `preview` (pre-download) shows the host-id / topics / actionable status
	 * columns; `loaded` (post-download) collapses to what landed in the CSV.
	 */
	variant?: 'preview' | 'loaded'
	/** Formatted generation time, stamped on every loaded row (`loaded` variant). */
	loadedAt?: string
	onMarkUnresolvable?: (row: MarqueeExportRow) => void
	/** Opens the assign-host dialog on a row that carries no host id. */
	onAssignHost?: (row: MarqueeExportRow) => void
	onRestoreResolution?: (row: MarqueeExportRow) => void
	/** Attendee id whose restore is in flight, so the spinner lands on its row. */
	restoringAttendeeId?: string | null
}

export function ExportPreviewTable({
	data,
	eventId,
	captureMode,
	variant = 'preview',
	loadedAt,
	onMarkUnresolvable,
	onAssignHost,
	onRestoreResolution,
	restoringAttendeeId
}: ExportPreviewTableProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const isLoaded = variant === 'loaded'

	const columns = useMemo(() => {
		const attendeeColumn = {
			id: 'attendee' as const,
			header: t('marquee:attendee_col_attendee'),
			cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => {
				const { name, company } = splitExportAttendee(dataItem.attendee)
				return (
					<div className='flex min-w-0 flex-col'>
						<AttendeeNameLink
							className='font-medium text-neutral-900 text-sm'
							name={name}
							to={attendeeDetailPath(
								eventId,
								dataItem.attendee_id,
								captureMode
							)}
						/>
						{company && (
							<TruncatedText className='text-neutral-500 text-sm'>
								{company}
							</TruncatedText>
						)}
					</div>
				)
			},
			size: 240
		}
		const childAccountColumn = {
			id: 'childAccount' as const,
			header: t('marquee:export_col_child_account'),
			cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => {
				if (dataItem.child_account) {
					return (
						<TruncatedText className='font-medium text-neutral-900 text-sm'>
							{dataItem.child_account}
						</TruncatedText>
					)
				}
				const placeholderKey = childAccountPlaceholderKey(dataItem)
				return (
					<span className='text-neutral-500 text-sm'>
						{placeholderKey ? t(placeholderKey) : '-'}
					</span>
				)
			},
			size: 210
		}
		if (isLoaded) {
			return [
				attendeeColumn,
				childAccountColumn,
				{
					id: 'loadStatus' as const,
					header: t('marquee:export_col_load_status'),
					cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => (
						<LoadStatusCell row={dataItem} />
					),
					size: 200
				},
				{
					id: 'loadedAt' as const,
					header: t('marquee:export_col_loaded_at'),
					// An excluded row never landed, so it carries no timestamp.
					cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => (
						<span className='text-neutral-700 text-sm tabular-nums'>
							{isExportRowUnresolvable(dataItem)
								? '-'
								: (loadedAt ?? '-')}
						</span>
					),
					size: 160
				}
			]
		}
		return [
			attendeeColumn,
			childAccountColumn,
			{
				id: 'hostId' as const,
				header: t('marquee:export_col_host_id'),
				cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => (
					<TruncatedText className='text-neutral-700 text-sm'>
						{dataItem.host_id ?? '-'}
					</TruncatedText>
				),
				size: 180
			},
			{
				id: 'ctaCount' as const,
				header: t('marquee:export_col_cta_count'),
				cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => (
					<CtaCountCell row={dataItem} />
				),
				size: 140
			},
			{
				id: 'status' as const,
				header: t('marquee:attendee_col_status'),
				cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => (
					<StatusBadge row={dataItem} />
				),
				size: 220
			},
			{
				id: 'actions' as const,
				header: t('common:actions'),
				// Sits over the buttons, which are anchored to the table's edge.
				headerAlign: 'end' as const,
				cell: ({ dataItem }: { dataItem: MarqueeExportRow }) => (
					<div className='flex w-full items-center justify-end'>
						<RowActions
							isRestoring={
								restoringAttendeeId === dataItem.attendee_id
							}
							onAssignHost={() => onAssignHost?.(dataItem)}
							onMarkUnresolvable={() =>
								onMarkUnresolvable?.(dataItem)
							}
							onRestoreResolution={() =>
								onRestoreResolution?.(dataItem)
							}
							row={dataItem}
						/>
					</div>
				),
				size: 260
			}
		]
	}, [
		t,
		isLoaded,
		loadedAt,
		onMarkUnresolvable,
		onAssignHost,
		onRestoreResolution,
		restoringAttendeeId,
		eventId,
		captureMode
	])

	if (data.length === 0) {
		return <TableEmpty />
	}

	if (!isDesktop) {
		return (
			<div className='flex flex-col gap-3'>
				{data.map((row, index) => (
					<ExportCard
						captureMode={captureMode}
						eventId={eventId}
						isLoaded={isLoaded}
						isRestoring={restoringAttendeeId === row.attendee_id}
						key={row.attendee_id ?? index}
						loadedAt={loadedAt}
						onAssignHost={() => onAssignHost?.(row)}
						onMarkUnresolvable={() => onMarkUnresolvable?.(row)}
						onRestoreResolution={() => onRestoreResolution?.(row)}
						row={row}
					/>
				))}
			</div>
		)
	}

	return (
		<TableScrollBox>
			{(scrollParent) => (
				<DataTable
					// The actions live in the status column, so it is pinned right
					// like the check-in roster's: the grid scrolls sideways on a
					// narrow desktop, and Mark unresolvable / Resolve have to stay
					// reachable without scrolling back. A no-op on the `loaded`
					// variant, which has no status column and no actions.
					columnPinning={{ left: [], right: ['actions'] }}
					columns={columns}
					customScrollParent={scrollParent}
					data={data}
				/>
			)}
		</TableScrollBox>
	)
}

function ExportCard({
	row,
	eventId,
	captureMode,
	isLoaded,
	loadedAt,
	onMarkUnresolvable,
	onAssignHost,
	onRestoreResolution,
	isRestoring
}: {
	row: MarqueeExportRow
	eventId?: string
	captureMode?: MarqueeCaptureMode
	isLoaded?: boolean
	loadedAt?: string
	onMarkUnresolvable?: () => void
	onAssignHost?: () => void
	onRestoreResolution?: () => void
	isRestoring?: boolean
}) {
	const { t } = useTranslation()
	const { name, company } = splitExportAttendee(row.attendee)
	const detailPath = attendeeDetailPath(eventId, row.attendee_id, captureMode)
	const childPlaceholderKey = childAccountPlaceholderKey(row)
	return (
		<div
			className={cn(
				'flex flex-col gap-3 rounded-2xl bg-neutral-white p-4 shadow-airbnb-3',
				!isLoaded &&
					isExportRowUnresolved(row) &&
					'border border-grey-200'
			)}
		>
			<div className='flex flex-col gap-3'>
				<div className='flex items-start justify-between gap-3'>
					<div className='flex min-w-0 flex-col'>
						<AttendeeNameLink
							className='font-medium text-neutral-900 text-sm'
							name={name}
							to={detailPath}
						/>
						{company && (
							<span className='truncate text-neutral-500 text-sm'>
								{company}
							</span>
						)}
					</div>
					{isLoaded && <LoadStatusCell row={row} />}
				</div>
				{/* The card keeps lane and actions stacked together: the split
				    into two columns is the desktop grid's layout, not a change
				    to what the row says. */}
				{!isLoaded && (
					<div className='flex flex-col items-start gap-3'>
						<RowActions
							isRestoring={isRestoring}
							onAssignHost={onAssignHost}
							onMarkUnresolvable={onMarkUnresolvable}
							onRestoreResolution={onRestoreResolution}
							row={row}
						/>
					</div>
				)}
			</div>
			<dl className='flex flex-col gap-1.5 text-sm'>
				{!isLoaded && (
					<CardRow label={t('marquee:attendee_col_status')}>
						<StatusBadge row={row} />
					</CardRow>
				)}
				<CardRow label={t('marquee:export_col_child_account')}>
					{row.child_account ??
						(childPlaceholderKey ? t(childPlaceholderKey) : '-')}
				</CardRow>
				{isLoaded ? (
					<CardRow label={t('marquee:export_col_loaded_at')}>
						{isExportRowUnresolvable(row) ? '-' : (loadedAt ?? '-')}
					</CardRow>
				) : (
					<>
						<CardRow label={t('marquee:export_col_host_id')}>
							{row.host_id ?? '-'}
						</CardRow>
						<CardRow label={t('marquee:export_col_cta_count')}>
							{(row.ctas ?? []).length}
						</CardRow>
					</>
				)}
			</dl>
		</div>
	)
}

function CardRow({
	label,
	children
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className='flex items-center justify-between gap-4'>
			<dt className='shrink-0 text-neutral-500'>{label}</dt>
			<dd className='flex min-w-0 justify-end break-words text-right text-neutral-900'>
				{children}
			</dd>
		</div>
	)
}
